import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TAB_ICON_SVG, TAB_ROUTES, type TabIcon } from '@tikkas/shared';
import { IC, Svg } from '../art/icons.js';
import { bagCount, bagSubtotal } from '../lib/bag.js';
import { fmt } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { featureOn, useStore } from '../lib/store.js';
import { accountIsTab, MAIN_SCREENS, tabForScreen, visibleTabs } from '../lib/appearance.js';
import { Logo, useLook } from '../lib/look.js';

/*
 * The tabs come from the admin (docs/26 §4): which ones, their order, names and icons. Today's are Home · Order · Account
 * (Saif, 21 Oct 2026 evening: Loyalty and Offers folded into Home; their tabs open Home when the admin adds them back). Catering shows only while its switch is on. Account is always reachable: a tab, or the
 * account button (on Home and the laptop bar, and at the top of every main screen while Account isn't a tab).
 */
const icon = (name: TabIcon) => (IC as Record<string, string>)[name] ?? TAB_ICON_SVG[name];

export function TabBar({ laptop }: { laptop: boolean }) {
  const { screen, route, navigate } = useNav();
  const s = useStore();
  const { v } = useLook();
  const count = bagCount(s.bag);
  const go = (path: string) => navigate(path, { root: true });
  const cur = tabForScreen(screen);
  const catering = featureOn(s, 'catering');
  const tabs = visibleTabs(v, catering);
  // The active highlight (.tabglide): one block whose transform is measured from the tabs' own boxes, so it glides straight
  // from the old tab to the new one (no stop at the tabs between) over 320 ms; re-measured when the bar resizes.
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    const bar = nav.current;
    if (!bar) return;
    const place = () => {
      const glide = bar.querySelector<HTMLElement>('.tabglide');
      const active = bar.querySelector<HTMLElement>('.tab[aria-current="page"]');
      if (!glide || !active) return;
      glide.style.width = `${active.offsetWidth}px`;
      glide.style.transform = `translate3d(${active.offsetLeft}px, 0, 0)`;
      glide.style.opacity = '1';
    };
    place();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    ro?.observe(bar);
    return () => ro?.disconnect();
  }, [cur, tabs.length, v.theme.tabBar]);
  // Every hook sits above this branch: when the viewport crosses 900px (a laptop window resized, an iPad turning) the same
  // component re-renders the other bar, and React needs the same hooks in the same order (the guest-checkout session hit
  // "Rendered fewer hooks than expected" here, 24 Oct 2026; test/tabbar.test.ts guards it).
  const onCateringTab = screen === 'cater' && tabs.some((t) => t.id === 'catering');
  const shows = MAIN_SCREENS.includes(screen) || onCateringTab;
  // The page keeps room under its last content while the bar is up (styles.css: html[data-bar] .pad).
  useEffect(() => {
    document.documentElement.toggleAttribute('data-bar', shows);
    return () => document.documentElement.removeAttribute('data-bar');
  }, [shows]);
  if (laptop) {
    const avatar = v.accountButton || !accountIsTab(v);
    // The logo is Home and the round button is Account (while it's on), so the links are the other tabs, in order.
    const links = tabs.filter((t) => t.id !== 'home' && !(t.id === 'account' && avatar));
    const L = (path: string, label: string, on: boolean, key: string) => <button type="button" className="tn-link" key={key} aria-current={on ? 'page' : undefined} onClick={() => go(path)}>{label}</button>;
    return (
      <nav id="tabbar" aria-label="Main">
        <button type="button" className="tn-logo" onClick={() => go('/')} aria-label="Tikkas home"><Logo /></button>
        <div className="tn-links">
          {links.map((t) => L(TAB_ROUTES[t.id], t.label, cur === t.id, t.id))}
          {/* Office managers order catering on a laptop: its link stays while the switch is on, tab or not. */}
          {catering && !links.some((t) => t.id === 'catering') ? L('/catering', 'Catering', cur === 'catering', 'catering') : null}
        </div>
        <span className="grow" />
        {/* Points and credit live on the Card tab only (Saif, 19 Oct 2026), so the bar doesn't repeat them. */}
        {s.me ? null : <button type="button" className="tn-link" onClick={() => navigate(`/join?next=${encodeURIComponent(route.path)}`)}>Sign in</button>}
        <button type="button" className="tn-bag" onClick={() => navigate('/bag')}><Svg html={IC.bag} /><span>Bag</span>{count ? <i>{count}</i> : null}</button>
        {avatar ? <button type="button" className="avatar" onClick={() => go('/account')} aria-label="Account">{s.me?.customer.initials ?? <Svg html={IC.user} />}</button> : null}
      </nav>
    );
  }
  if (!shows) return null;
  // The floating frosted pill (theme.tabBar "pill", Saif 21 Oct) or the plain bar on the bottom edge ("standard"). Drawn on
  // body, outside the app shell: a transform, filter or overflow clip on any ancestor would silently drop the glass's blur in
  // WebKit (the tab slide, the glide intro and the sheets all transform), and here nothing paints between it and the page.
  return createPortal(
    <nav id="tabbar" className={v.theme.tabBar === 'pill' ? 'pill glass' : ''} aria-label="Main" ref={nav}>
      <span className="tabglide" aria-hidden="true" />
      {tabs.map((t) => (
        // Every tab alike (the raised centre disc went on 21 Oct).
        <button type="button" className="tab" key={t.id} aria-current={cur === t.id ? 'page' : undefined} onClick={() => go(TAB_ROUTES[t.id])}><Svg html={icon(t.icon)} /><span className="tlabel">{t.label}</span></button>
      ))}
    </nav>,
    document.body,
  );
}

/** The round Account button. */
export function AccountButton() {
  const s = useStore();
  const { navigate } = useNav();
  const u = s.me?.customer;
  return <button className="avatar" type="button" onClick={() => navigate(u ? '/account' : '/join?next=/account', { root: !!u })} aria-label="Account">{u ? u.initials : <Svg html={IC.user} />}</button>;
}

/** A main screen's heading, with anything beside it, and the Account button while Account isn't a tab. */
export function PageHead({ title, children, style }: { title: string; children?: ReactNode; style?: CSSProperties }) {
  const { v } = useLook();
  const acct = !accountIsTab(v);
  return (
    <div className="pagehead">
      <h1 className="display" style={style}>{title}</h1>
      {children || acct ? <span className="row" style={{ gap: 10 }}>{children}{acct ? <AccountButton /> : null}</span> : null}
    </div>
  );
}

export function BagBar() {
  const s = useStore();
  const { navigate } = useNav();
  const { t } = useLook();
  const count = bagCount(s.bag);
  if (!count || !s.menuData) return null;
  return (
    <button type="button" className={`bagbar amberpill ${s.bump ? 'bump' : ''}`} onClick={() => navigate('/bag')}>
      <span className="count">{count}</span><span>{s.bump ? 'Added to bag' : t('button.view_bag')}</span><span className="tnum bagtot">{fmt(bagSubtotal(s.menuData.menu, s.bag))}</span>
    </button>
  );
}
